import { config } from '#~/config/config.js';
import type { Room } from '@studio/models/room';
import type { T_Any, T_Maybe } from '@tryiris-ai/js-types';

/**
 * Speculatively warm up TCP+TLS to the LiveKit endpoint while ProfileSync
 * is still fetching user/room data. Saves 800-1500ms of connection latency
 * by overlapping the handshake with the registry fetch.
 *
 * Uses AbortController so the caller can cancel if the room ID changes
 * after registry loads (stale pre-connect against wrong room).
 */
export function speculativeLiveKitPreConnect(
  roomId: T_Maybe<Room['id']>,
  signal: AbortSignal
): void {
  if (!roomId) return;

  // liveKit.endpoint exists at runtime (set in config/utils.ts) but is
  // commented out in the Config type — access via cast to avoid TS error.
  const endpoint = (config as T_Any).liveKit?.endpoint as T_Maybe<string>;
  if (!endpoint) {
    console.info('[speculativeLiveKitPreConnect] no endpoint configured, skipping');
    return;
  }

  if (signal.aborted) return;

  console.info(`[speculativeLiveKitPreConnect] initiated for room=${roomId}`);

  // Insert a <link rel="preconnect"> to warm DNS + TCP + TLS.
  // Derive origin from the endpoint (handles wss://, https://, bare hostname).
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = endpoint.includes('://') ? endpoint : `https://${endpoint}`;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);

  // Cleanup on abort: remove the hint since it's no longer valid.
  const onAbort = () => {
    console.info('[speculativeLiveKitPreConnect] abandoned: abort signal received');
    link.remove();
  };

  if (signal.aborted) {
    onAbort();
    return;
  }

  signal.addEventListener('abort', onAbort, { once: true });

  console.info(
    `[speculativeLiveKitPreConnect] upgraded: preconnect link injected for ${link.href}`
  );
}
